import type { ColumnType } from "antd/es/table"

/**
 * A saved Finance layout lists every column in the order it was saved, pinned
 * ones included, and pinned columns cannot be dragged or resized. A layout from
 * before the name and amount were pinned would otherwise leave them mid-table at
 * an old width the user has no handle to change. So the pinned-left columns are
 * moved to the front, in their declared order and at their declared width, and
 * the pinned-right ones to the end; everything else keeps the saved order.
 */
export function keepPinnedColumnsInPlace<T>(
  configured: ColumnType<T>[],
  declared: ColumnType<T>[]
): ColumnType<T>[] {
  const declaredIndex = new Map(
    declared.map((column, index) => [String(column.key), index])
  )
  const declaredByKey = new Map(
    declared.map((column) => [String(column.key), column])
  )

  const left = configured
    .filter((column) => column.fixed === "left")
    .sort(
      (a, b) =>
        (declaredIndex.get(String(a.key)) ?? 0) -
        (declaredIndex.get(String(b.key)) ?? 0)
    )
    .map((column) => ({
      ...column,
      width: declaredByKey.get(String(column.key))?.width ?? column.width
    }))
  const right = configured.filter((column) => column.fixed === "right")
  const scrolling = configured.filter(
    (column) => column.fixed !== "left" && column.fixed !== "right"
  )

  return [...left, ...scrolling, ...right]
}
